import { Component, Inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiDecryptService } from '../_services/pii-decrypt.service';
import { PiiAuditService, AuditReason } from '../_services/pii-audit.service';
import { AlertService } from '../_services/alert.service';
import { PiiUnhideModalComponent } from './pii-unhide-modal/pii-unhide-modal.component';
import { StorageService } from '../_services/storage.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-pii-cell-renderer',
  template: `
    <div class="pii-cell-container">
      <span class="pii-value" [class.masked]="!isCurrentlyUnmasked()" [class.unmasked]="isCurrentlyUnmasked()">
        {{ getDisplayValue() }}
      </span>
    </div>
  `,
  styles: [`
    .pii-cell-container {
      display: flex;
      align-items: center;
      height: 100%;
    }
    .pii-value {
      font-family: inherit;
      font-size: inherit;
      padding: 0;
      border-radius: 0;
      display: inline-block;
    }
    .pii-value.masked {
      color: inherit;
      /* Normal text appearance for masked values */
    }
    .pii-value.unmasked {
      color: inherit;
      background-color: transparent;
      font-weight: inherit;
      border: none;
      padding: 0;
      border-radius: 0;
    }
    .btn {
      border-radius: 0.25rem;
      font-size: 0.8rem;
      padding: 0.25rem 0.5rem;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .pii-unmask-btn {
      color: #0d6efd;
      border-color: #0d6efd;
      background-color: transparent;
    }
    .pii-unmask-btn:hover:not(:disabled) {
      background-color: #0d6efd;
      color: white;
    }
    .pii-unmask-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .pii-mask-btn {
      color: #6c757d;
      border-color: #6c757d;
      background-color: transparent;
    }
    .pii-mask-btn:hover {
      background-color: #6c757d;
      color: white;
    }
    .btn i {
      font-size: 0.8rem;
    }
  `]
})
export class PiiCellRendererComponent implements ICellRendererAngularComp {
  private params!: ICellRendererParams;
  public isRowUnmasked: boolean = false; // State for the entire row
  private originalValue: string = '';
  private maskedValue: string = '';
  private decryptedValue: string = '';
  private decryptionSuccessful: boolean = false; // Track if decryption was successful

  constructor(
    private piiDecryptService: PiiDecryptService,
    private piiAuditService: PiiAuditService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private storageService: StorageService,
    private router: Router
  ) {
    // Add a global test method for debugging
    (window as any).testPiiDecryption = this.globalTestDecryption.bind(this);
    (window as any).enablePiiTest = false; // Set to true to enable automatic testing
    (window as any).debugTableData = this.debugTableData.bind(this);
    (window as any).testTransactionEntityDetection = this.testTransactionEntityDetection.bind(this);
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.originalValue = params.value || '';
    
    // Check if the entire row is unmasked
    this.isRowUnmasked = params.data._piiUnmasked || false;
    
    // Decrypt and mask data exactly like PII field component
    this.decryptAndMaskData();
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.originalValue = params.value || '';
    
    // Check if the entire row is unmasked
    this.isRowUnmasked = params.data._piiUnmasked || false;
    
    // Re-decrypt and mask data
    this.decryptAndMaskData();
    
    return true;
  }

  /**
   * Test encryption/decryption cycle to verify it's working
   */
  private testEncryptionDecryption(): void {
    console.log('🧪 === Testing Encryption/Decryption Cycle ===');
    
    // Test with a simple string first
    const testValue = 'Successful';
    console.log('🔵 Original:', testValue);
    
    // Encrypt it (simulate what backend does)
    const encrypted = this.encryptForTest(testValue);
    console.log('🔴 Encrypted:', encrypted);
    
    // Decrypt it
    const decrypted = this.piiDecryptService.decrypt(encrypted);
    console.log('🟢 Decrypted:', decrypted);
    console.log('✅ Test passed:', testValue === decrypted);
    
    // Also test with the actual value from the cell
    if (this.originalValue) {
      console.log('🔵 Testing actual cell value:', this.originalValue);
      const actualDecrypted = this.piiDecryptService.decrypt(this.originalValue);
      console.log('🟢 Actual decrypted:', actualDecrypted);
    }
    
    console.log('🧪 === End Test ===');
  }

  /**
   * Encrypt a value for testing (same logic as customer service)
   */
  private encryptForTest(value: string): string {
    if (!value) return '';
    
    const secretKey = environment.piiSecretKey || 'test-key-123';
    const encoder = new TextEncoder();
    
    try {
      const textBytes = encoder.encode(value);
      const keyBytes = encoder.encode(secretKey);
      
      // XOR encryption
      const encrypted = new Uint8Array(textBytes.length);
      for (let i = 0; i < textBytes.length; i++) {
        encrypted[i] = textBytes[i] ^ keyBytes[i % keyBytes.length];
      }
      
      // Convert to base64 for safe transmission
      return btoa(String.fromCharCode(...encrypted));
    } catch (error) {
      console.error('Test encryption failed:', error);
      return value;
    }
  }

  /**
   * Decrypt the encrypted data and generate masked version on component load
   * Updated for unified encryption approach: all encrypted data decrypts to strings
   */
  private decryptAndMaskData(): void {
    if (this.originalValue != null && this.originalValue !== '') {
      const originalString = this.originalValue.toString();
      
      // Check if this is a plain numeric value (not encrypted - legacy case)
      if (typeof this.originalValue === 'number' || 
         (typeof this.originalValue === 'string' && this.isNumericString(originalString) && !this.piiDecryptService.isEncrypted(originalString))) {
        // Handle plain numeric values - just mask them
        this.decryptedValue = originalString;
        this.maskedValue = this.maskNumericValue(originalString);
        this.decryptionSuccessful = true;
      }
      // Check if this looks like encrypted data (unified approach)
      else if (typeof this.originalValue === 'string' && this.piiDecryptService.isEncrypted(originalString)) {
        // Decrypt the data - will always return a string
        const decryptedValue = this.piiDecryptService.decrypt(originalString);
        
        // Check if decryption failed
        if (decryptedValue === '[DECRYPTION ERROR]' || !decryptedValue) {
          this.decryptedValue = originalString;
          this.maskedValue = this.createFallbackMask(originalString);
          this.decryptionSuccessful = false;
          console.error('PII decryption failed for field:', this.params?.colDef?.field);
        } else {
          this.decryptedValue = decryptedValue;
          
          // Check if decrypted string represents a numeric value (BigDecimal amount)
          if (this.isNumericString(decryptedValue)) {
            // This is an encrypted amount - mask it as numeric
            this.maskedValue = this.maskNumericValue(decryptedValue);
            this.decryptionSuccessful = true;
          }
          // Check if it's other PII data (Emirates ID, IBAN, etc.)
          else if (this.containsWeirdCharacters(decryptedValue)) {
            this.maskedValue = this.createFallbackMask(decryptedValue);
            this.decryptionSuccessful = false;
          } else {
            if (this.params?.colDef?.field === 'emiratesId' || this.params?.colDef?.field?.toLowerCase().includes('eid')) {
              this.maskedValue = 'X'.repeat(18);
            } else {
              this.maskedValue = this.piiDecryptService.getMaskedData(decryptedValue, 3, 1);
            }
            this.decryptionSuccessful = true;
          }
        }
      } else {
        // Plain text data (not encrypted)
        this.decryptedValue = originalString;
        if (this.containsWeirdCharacters(originalString)) {
          this.maskedValue = this.createFallbackMask(originalString);
          this.decryptionSuccessful = false;
        } else {
          if (this.params?.colDef?.field === 'emiratesId' || this.params?.colDef?.field?.toLowerCase().includes('eid')) {
            this.maskedValue = 'X'.repeat(18);
          } else {
            this.maskedValue = this.piiDecryptService.getMaskedData(originalString, 3, 1);
          }
          this.decryptionSuccessful = true;
        }
      }
    } else {
      // Handle null/empty values
      this.decryptedValue = '';
      this.maskedValue = '';
    }
    
    // Start with masked state
    this.isRowUnmasked = false;
  }

  /**
   * Check if a string contains weird/non-printable characters
   */
  private containsWeirdCharacters(str: string): boolean {
    // Check for non-printable ASCII characters (except common whitespace)
    const hasNonPrintable = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/.test(str);
    
    // Check for too many special characters
    const specialCharCount = (str.match(/[^\w\s.-]/g) || []).length;
    const hasExcessiveSpecialChars = specialCharCount > str.length * 0.3;
    
    return hasNonPrintable || hasExcessiveSpecialChars;
  }

  /**
   * Create a fallback mask for data with weird characters
   */
  private createFallbackMask(data: string): string {
    if (!data || data.trim() === '') {
      return '';
    }
    
    const cleanValue = data.replace(/[^0-9]/g, '');
    
    if (this.params?.colDef?.field === 'emiratesId' || this.params?.colDef?.field?.toLowerCase().includes('eid')) {
      return 'X'.repeat(18);
    }
    
    if (cleanValue.length >= 15) {
      return 'X'.repeat(18);
    } else if (cleanValue.length >= 10) {
      return 'XXX-XXXX-XXX';
    } else if (cleanValue.length >= 5) {
      return 'XX-XXX-XX';
    } else {
      return 'X'.repeat(Math.max(cleanValue.length, 3));
    }
  }

  /**
   * Check if the row is currently unmasked
   */
  isCurrentlyUnmasked(): boolean {
    return this.params?.data?._piiUnmasked || false;
  }
  
  /**
   * Get display value based on current state
   */
  getDisplayValue(): string {
    if (!this.originalValue || this.originalValue.toString().trim() === '') {
      return '';
    }
    
    const currentUnmaskedState = this.params?.data?._piiUnmasked || false;
    
    if (currentUnmaskedState) {
      let displayValue = this.decryptedValue || this.maskedValue || '';
      
      if ((this.params?.colDef?.field === 'emiratesId' || this.params?.colDef?.field?.toLowerCase().includes('eid')) && displayValue) {
        displayValue = this.formatEmiratesIdForDisplay(displayValue);
      }
      
      return displayValue;
    } else {
      return this.maskedValue || '';
    }
  }

  private formatEmiratesIdForDisplay(value: string): string {
    if (!value) return '';
    
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    if (cleanValue.length === 15) {
      return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 7)}-${cleanValue.slice(7, 14)}-${cleanValue.slice(14)}`;
    }
    
    return value;
  }

  private getUserId(): string {
    // Get user ID from storage or context
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsed = JSON.parse(userInfo);
        return parsed.userId || parsed.id || 'Unknown';
      } catch (error) {
        return 'Unknown';
      }
    }
    return 'Unknown';
  }

  /**
   * Get the entity ID (customer ID, merchant ID, transaction ID, etc.) from row data
   */
  private getEntityId(): string {
    if (this.params?.data) {
      const entityType = this.getEntityType();
      const rowData = this.params.data;
      
      // Handle transaction-specific entity types
      switch (entityType) {
        case 'cma_statement':
          return rowData.txnId || 
                 rowData.transactionId ||
                 rowData.refNumber ||
                 rowData.accountNumber ||
                 rowData.id || 
                 'unknown';
                 
        case 'super_transaction':
          return rowData.txnId || 
                 rowData.transactionId ||
                 rowData.id ||
                 rowData.senderWalletId ||
                 rowData.receiverWalletId ||
                 'unknown';
                 
        case 'wallet_transaction':
          return rowData.txnId || 
                 rowData.transactionId ||
                 rowData.walletId ||
                 rowData.mobileNumber ||
                 rowData.id || 
                 'unknown';
                 
        case 'transaction':
        case 'merchant_transaction':
          return rowData.txnId || 
                 rowData.transactionId ||
                 rowData.orderId ||
                 rowData.refNumber ||
                 rowData.paymentId ||
                 rowData.id || 
                 'unknown';
                 
        case 'settlement':
          return rowData.settlementId || 
                 rowData.txnId ||
                 rowData.transactionId ||
                 rowData.batchId ||
                 rowData.id || 
                 'unknown';
                 
        case 'reconciliation':
          return rowData.reconciliationId || 
                 rowData.txnId ||
                 rowData.transactionId ||
                 rowData.refNumber ||
                 rowData.id || 
                 'unknown';
                 
        case 'merchant':
          return rowData.payId || 
                 rowData.merchantId || 
                 rowData.merchantCode ||
                 rowData.id || 
                 rowData.mobileNumber || 
                 'unknown';
                 
        case 'reseller':
          return rowData.resellerId || 
                 rowData.resellerCode ||
                 rowData.partnerId ||
                 rowData.id || 
                 rowData.mobileNumber || 
                 'unknown';
                 
        case 'customer':
        default:
          return rowData.id || 
                 rowData.userId || 
                 rowData.customerId || 
                 rowData.mobileNumber || 
                 'unknown';
      }
    }
    return 'unknown';
  }

  /**
   * Get transaction ID from row data for transaction-related contexts
   */
  private getTransactionId(): string {
    if (this.params?.data) {
      const rowData = this.params.data;
      
      // Try multiple possible transaction ID fields
      return rowData.txnId || 
             rowData.transactionId ||
             rowData.orderId ||
             rowData.paymentId ||
             rowData.refNumber ||
             rowData.settlementId ||
             '';
    }
    return '';
  }

  /**
   * Get the most appropriate reference ID for audit trail
   */
  private getReferenceId(): string {
    const entityType = this.getEntityType();
    const transactionId = this.getTransactionId();
    
    // For transaction-related contexts, prioritize transaction ID
    if (entityType.includes('transaction') || entityType === 'settlement' || entityType === 'cma_statement') {
      if (transactionId) {
        return transactionId;
      }
    }
    
    // For non-transaction contexts, use entity ID
    return this.getEntityId();
  }

  /**
   * Get current user performing the action
   */
  private getCurrentUser(): string {
    try {
      const currentUser = this.storageService.getUserName();
    
      return currentUser || 'unknown';
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return 'unknown';
  }

  /**
   * Get entity type based on the context (customer, merchant, reseller, transaction, etc.)
   */
  private getEntityType(): string {
    // Method 1: Detect based on current route
    const currentRoute = this.router.url.toLowerCase();
    
    // Transaction-specific routes
    if (currentRoute.includes('cma-statement')) {
      return 'cma_statement';
    } else if (currentRoute.includes('super-transaction')) {
      return 'super_transaction';
    } else if (currentRoute.includes('wallet-ledger')) {
      return 'wallet_transaction';
    } else if (currentRoute.includes('transaction-report')) {
      return 'transaction';
    } else if (currentRoute.includes('merchant-transaction')) {
      return 'merchant_transaction';
    } else if (currentRoute.includes('settlement-report')) {
      return 'settlement';
    } else if (currentRoute.includes('reconciliation')) {
      return 'reconciliation';
    }
    
    // Entity-specific routes
    if (currentRoute.includes('merchant')) {
      return 'merchant';
    } else if (currentRoute.includes('reseller')) {
      return 'reseller';
    } else if (currentRoute.includes('customer')) {
      return 'customer';
    }
    
    // Method 2: Detect based on row data structure
    if (this.params?.data) {
      const rowData = this.params.data;
      
      // Check for transaction-specific fields
      if (rowData.hasOwnProperty('txnId') || 
          rowData.hasOwnProperty('transactionId') ||
          rowData.hasOwnProperty('refNumber') ||
          rowData.hasOwnProperty('orderId')) {
        
        // Further refine transaction type
        if (rowData.hasOwnProperty('accountNumber') && 
            rowData.hasOwnProperty('runningBalance')) {
          return 'cma_statement';
        } else if (rowData.hasOwnProperty('walletId') && 
                   rowData.hasOwnProperty('openingBal')) {
          return 'wallet_transaction';
        } else if (rowData.hasOwnProperty('senderWalletId') && 
                   rowData.hasOwnProperty('receiverWalletId')) {
          return 'super_transaction';
        } else if (rowData.hasOwnProperty('settlementId') || 
                   rowData.hasOwnProperty('settlementDate')) {
          return 'settlement';
        } else {
          return 'transaction';
        }
      }
      
      // Check for merchant-specific fields
      if (rowData.hasOwnProperty('payId') || 
          rowData.hasOwnProperty('merchantId') ||
          rowData.hasOwnProperty('businessName') ||
          rowData.hasOwnProperty('merchantName') ||
          rowData.hasOwnProperty('acquirer')) {
        return 'merchant';
      }
      
      // Check for reseller-specific fields
      if (rowData.hasOwnProperty('resellerId') ||
          rowData.hasOwnProperty('resellerName') ||
          rowData.hasOwnProperty('partnerType')) {
        return 'reseller';
      }
      
      // Check for customer-specific fields
      if (rowData.hasOwnProperty('customerId') ||
          rowData.hasOwnProperty('emiratesId') ||
          rowData.hasOwnProperty('kycStatus') ||
          rowData.hasOwnProperty('screeningId')) {
        return 'customer';
      }
    }
    
    // Method 3: Detect based on grid column structure
    if (this.params?.columnApi) {
      const columnDefs = this.params.columnApi.getColumns();
      const fieldNames = columnDefs?.map(col => col.getColDef().field?.toLowerCase()) || [];
      
      // Check for transaction-specific columns
      if (fieldNames.some(field => field?.includes('txnid') || 
                                  field?.includes('transactionid') ||
                                  field?.includes('walletid') ||
                                  field?.includes('accountnumber') ||
                                  field?.includes('runningbalance'))) {
        return 'transaction';
      }
      
      // Check for merchant-specific columns
      if (fieldNames.some(field => field?.includes('payid') || 
                                  field?.includes('merchant') ||
                                  field?.includes('business') ||
                                  field?.includes('acquirer'))) {
        return 'merchant';
      }
      
      // Check for reseller-specific columns
      if (fieldNames.some(field => field?.includes('reseller') ||
                                  field?.includes('partner'))) {
        return 'reseller';
      }
    }
    
    // Default to customer if no specific indicators found
    return 'customer';
  }

  /**
   * Get entity mobile number from row data
   */
  private getEntityMobile(): string {
    if (this.params?.data) {
      const entityType = this.getEntityType();
      const rowData = this.params.data;
      
      // Handle transaction-specific entity types
      switch (entityType) {
        case 'cma_statement':
          return rowData.mobileNumber || 
                 rowData.mobile ||
                 rowData.contactNumber ||
                 rowData.accountHolderMobile ||
                 rowData.customerMobile ||
                 'unknown';
                 
        case 'super_transaction':
          return rowData.senderMobileNumber || 
                 rowData.receiverMobileNumber ||
                 rowData.mobileNumber ||
                 rowData.mobile ||
                 rowData.contactNumber ||
                 'unknown';
                 
        case 'wallet_transaction':
          return rowData.mobileNumber || 
                 rowData.mobile ||
                 rowData.sender ||
                 rowData.receiver ||
                 rowData.contactNumber ||
                 'unknown';
                 
        case 'transaction':
        case 'merchant_transaction':
          return rowData.customerMobile || 
                 rowData.mobileNumber ||
                 rowData.mobile ||
                 rowData.senderMobile ||
                 rowData.receiverMobile ||
                 rowData.contactNumber ||
                 'unknown';
                 
        case 'settlement':
          return rowData.merchantMobile || 
                 rowData.mobileNumber ||
                 rowData.mobile ||
                 rowData.contactNumber ||
                 'unknown';
                 
        case 'reconciliation':
          return rowData.mobileNumber || 
                 rowData.mobile ||
                 rowData.customerMobile ||
                 rowData.merchantMobile ||
                 rowData.contactNumber ||
                 'unknown';
                 
        case 'merchant':
          return rowData.contactNumber || 
                 rowData.merchantMobile ||
                 rowData.businessPhone ||
                 rowData.phone ||
                 rowData.mobileNumber || 
                 rowData.mobile || 
                 'unknown';
                 
        case 'reseller':
          return rowData.contactNumber || 
                 rowData.resellerMobile ||
                 rowData.partnerPhone ||
                 rowData.phone ||
                 rowData.mobileNumber || 
                 rowData.mobile || 
                 'unknown';
                 
        case 'customer':
        default:
          return rowData.mobileNumber || 
                 rowData.mobile || 
                 rowData.contactNumber || 
                 rowData.edcMobile || 
                  rowData.phone ||
                 'unknown';
      }
    }
    return 'unknown';
  }

  /**
   * Check if a value is a numeric string (including BigDecimal format)
   */
  private isNumericString(str: string): boolean {
    if (!str || str.trim() === '') return false;
    
    // Check for valid number patterns including decimals and negative numbers
    const numericPattern = /^-?\d+(\.\d+)?$/;
    return numericPattern.test(str.trim());
  }

  /**
   * Create masked version for numeric values (amounts) - completely hide the number
   */
  private maskNumericValue(value: string): string {
    if (!value || value.trim() === '') return '';
    
    const cleanValue = value.trim();
    
    // For numeric values, completely mask them for security
    if (cleanValue.includes('.')) {
      const [integerPart, decimalPart] = cleanValue.split('.');
      // Mask integer part completely, show .XX for decimal part
      return 'X'.repeat(Math.max(integerPart.length, 3)) + '.XX';
    } else {
      // For integer amounts, mask completely
      return 'X'.repeat(Math.max(cleanValue.length, 3));
    }
  }

  /**
   * Global test method that can be called from browser console
   */
  globalTestDecryption(testValue: string = 'Successful'): void {
    console.log('🧪 === Global PII Test ===');
    console.log('🔵 Test value:', testValue);
    
    // Encrypt it
    const encrypted = this.encryptForTest(testValue);
    console.log('🔴 Encrypted:', encrypted);
    
    // Decrypt it
    const decrypted = this.piiDecryptService.decrypt(encrypted);
    console.log('🟢 Decrypted:', decrypted);
    console.log('✅ Test passed:', testValue === decrypted);
    
    // Test with a known encrypted value
    const knownEncrypted = 'LwsHRjwNRk96XAZNUVhHVw==';
    console.log('🔵 Testing known encrypted value:', knownEncrypted);
    const knownDecrypted = this.piiDecryptService.decrypt(knownEncrypted);
    console.log('🟢 Known decrypted:', knownDecrypted);
    
    console.log('🧪 === End Global Test ===');
  }

  /**
   * Debug method to test table data specifically
   */
  debugTableData(): void {
    console.log('🧪 === Debug Table Data ===');
    
    // Get the AG-Grid instance
    const gridElements = document.querySelectorAll('ag-grid-angular');
    if (gridElements.length > 0) {
      console.log('📊 Found AG-Grid elements:', gridElements.length);
      
      // Try to get row data
      try {
        const gridApi = (gridElements[0] as any).gridOptions?.api;
        if (gridApi) {
          console.log('📊 Grid API found');
          
          const rowData: any[] = [];
          gridApi.forEachNode((node: any) => rowData.push(node.data));
          
          console.log('📊 Total rows:', rowData.length);
          
          if (rowData.length > 0) {
            const firstRow = rowData[0];
            console.log('📊 First row data:', firstRow);
            console.log('📊 Emirates ID in first row:', firstRow.emiratesId);
            
            if (firstRow.emiratesId) {
              console.log('🔍 Testing Emirates ID from table:');
              console.log('🔍 Raw value:', firstRow.emiratesId);
              console.log('🔍 Type:', typeof firstRow.emiratesId);
              console.log('🔍 Length:', firstRow.emiratesId.length);
              
              // Test decryption
              const decrypted = this.piiDecryptService.decrypt(firstRow.emiratesId);
              console.log('🔓 Decrypted:', decrypted);
              
              // Test masking
              const masked = this.piiDecryptService.getMaskedData(decrypted, 3, 1);
              console.log('🎭 Masked:', masked);
              
              // Test fallback mask
              const fallback = this.createFallbackMask(firstRow.emiratesId);
              console.log('🎭 Fallback mask:', fallback);
            }
          }
        } else {
          console.log('❌ Grid API not found');
        }
      } catch (error) {
        console.log('❌ Error accessing grid data:', error);
      }
    } else {
      console.log('❌ No AG-Grid elements found');
    }
    
    console.log('🧪 === End Debug ===');
  }

  /**
   * Test method to demonstrate entity type detection for various transaction scenarios
   */
  testTransactionEntityDetection(): void {
    console.log('🧪 === Testing Transaction Entity Detection ===');
    
    // Test CMA Statement scenario
    const cmaData = {
      txnId: 'TXN123456',
      accountNumber: 'AE123456789',
      amount: 'VFRdVlU=', // Encrypted amount
      runningBalance: 'UUpDVlVY', // Encrypted balance
      mobileNumber: '+971501234567'
    };
    
    // Test Super Transaction scenario
    const superTxnData = {
      txnId: 'STX789012',
      senderWalletId: 'WLT001',
      receiverWalletId: 'WLT002',
      totalAmount: 'QUJDREVGRw==', // Encrypted amount
      senderMobileNumber: '+971507654321',
      receiverMobileNumber: '+971509876543'
    };
    
    // Test Wallet Ledger scenario
    const walletData = {
      txnId: 'WTX345678',
      walletId: 'WLT003',
      mobileNumber: '+971505555555',
      amount: 'WFlaWmJj', // Encrypted amount
      openingBal: 'T3BlbkJhbA==', // Encrypted balance
      closingBal: 'Q2xvc2VCYWw=' // Encrypted balance
    };
    
    // Test each scenario
    const testScenarios = [
      { name: 'CMA Statement', data: cmaData, expectedType: 'cma_statement' },
      { name: 'Super Transaction', data: superTxnData, expectedType: 'super_transaction' },
      { name: 'Wallet Transaction', data: walletData, expectedType: 'wallet_transaction' }
    ];
    
    testScenarios.forEach(scenario => {
      console.log(`\n🔍 Testing ${scenario.name}:`);
      console.log('📊 Sample data:', scenario.data);
      
             // Create mock params
       const mockParams = {
         data: scenario.data,
         colDef: { field: 'amount' },
         value: (scenario.data as any).amount || (scenario.data as any).totalAmount || 'test-value'
       };
      
      // Temporarily set params
      const originalParams = this.params;
      this.params = mockParams as any;
      
      try {
        // Test entity type detection
        const detectedType = this.getEntityType();
        console.log('🎯 Detected entity type:', detectedType);
        console.log('✅ Expected type:', scenario.expectedType);
        console.log('✅ Match:', detectedType === scenario.expectedType);
        
        // Test entity ID extraction
        const entityId = this.getEntityId();
        console.log('🆔 Entity ID:', entityId);
        
        // Test entity mobile extraction
        const entityMobile = this.getEntityMobile();
        console.log('📱 Entity mobile:', entityMobile);
        
        // Test audit data preparation
        const auditData = {
          userId: entityId,
          fieldName: 'amount',
          reasonId: 'RC001',
          comment: 'Test unmask',
          accessedBy: 'test-user',
          timestamp: new Date().toISOString(),
          context: 'cell',
          entityType: detectedType,
          entityMobile: entityMobile
        };
        console.log('📝 Audit data:', auditData);
        
      } catch (error) {
        console.log('❌ Error testing scenario:', error);
      } finally {
        // Restore original params
        this.params = originalParams;
      }
    });
    
    console.log('\n🧪 === End Transaction Entity Detection Test ===');
  }
} 